import type { AppContext, ViewCleanup } from "../app";
import { loadState, saveState, resetState } from "../state/storage";
import {
  claimUsername,
  createPairCode,
  disconnect,
  isSynced,
  pushLeaderboardBests,
  redeemPairCode,
  register,
} from "../state/sync";

export const mountSettings = async (
  root: HTMLElement,
  ctx: AppContext,
): Promise<ViewCleanup> => {
  root.classList.add("view-settings");
  ctx.globe.setTargetIso(null);
  ctx.globe.setHoverEnabled(true);

  let state = loadState();
  const initialTolerance = state.settings.fuzzyMatchTolerance;
  const initialHints = state.settings.showHints;

  const card = document.createElement("section");
  card.className = "settings-card";
  card.innerHTML = `
    <header class="settings-header">
      <button type="button" class="settings-back" aria-label="Back">← Back</button>
      <h1>Settings</h1>
    </header>

    <section class="settings-section">
      <label class="settings-row">
        <span class="settings-label">Typo tolerance</span>
        <span class="settings-meta">
          <input class="settings-tolerance" type="range" min="0" max="5" step="1" value="${initialTolerance}" />
          <span class="settings-tolerance-value">${initialTolerance}</span>
        </span>
      </label>
      <p class="settings-hint">How forgiving the fuzzy matcher is. 0 = exact spelling; 5 = very lenient.</p>
    </section>

    <section class="settings-section">
      <label class="settings-row">
        <span class="settings-label">Show "Hint" button</span>
        <input class="settings-hints" type="checkbox" ${initialHints ? "checked" : ""} />
      </label>
      <p class="settings-hint">Reveals the first letter of the country name. Using a hint keeps your level neutral for that question.</p>
    </section>

    <section class="settings-section settings-sync">
      <h2 class="settings-subtitle">Cloud sync</h2>
      <div class="settings-sync-body"></div>
      <p class="settings-status" aria-live="polite"></p>
    </section>

    <section class="settings-section settings-leaderboard">
      <h2 class="settings-subtitle">Global leaderboard</h2>
      <div class="settings-leaderboard-body"></div>
      <p class="settings-leaderboard-status" aria-live="polite"></p>
    </section>

    <section class="settings-section">
      <h2 class="settings-subtitle">Danger zone</h2>
      <div class="settings-actions">
        <button type="button" class="settings-reset is-danger">Reset all stats</button>
      </div>
    </section>
  `;
  root.appendChild(card);

  const back = card.querySelector(".settings-back") as HTMLButtonElement;
  const toleranceInput = card.querySelector(
    ".settings-tolerance",
  ) as HTMLInputElement;
  const toleranceValue = card.querySelector(
    ".settings-tolerance-value",
  ) as HTMLSpanElement;
  const hintsInput = card.querySelector(
    ".settings-hints",
  ) as HTMLInputElement;
  const syncBody = card.querySelector(".settings-sync-body") as HTMLDivElement;
  const status = card.querySelector(".settings-status") as HTMLParagraphElement;
  const lbBody = card.querySelector(".settings-leaderboard-body") as HTMLDivElement;
  const lbStatus = card.querySelector(".settings-leaderboard-status") as HTMLParagraphElement;
  const resetBtn = card.querySelector(".settings-reset") as HTMLButtonElement;

  const flash = (msg: string, kind: "ok" | "err" = "ok"): void => {
    status.textContent = msg;
    status.dataset.kind = kind;
  };

  // ---- sync UI ------------------------------------------------------------

  // Shared: mint a fresh pair code and render it into the given box.
  // Called both from the synced-state "Create sync code" button and from
  // the unsynced-state "Create sync code for new device" flow (where it
  // runs immediately after register()).
  const showSyncCode = async (codeBox: HTMLDivElement): Promise<void> => {
    try {
      const { code, expiresAt } = await createPairCode();
      const minutes = Math.max(
        1,
        Math.round((new Date(expiresAt).getTime() - Date.now()) / 60000),
      );
      codeBox.hidden = false;
      codeBox.innerHTML = `
        <p class="settings-hint">Enter this on your other device under "Enter sync code from another device":</p>
        <div class="settings-sync-codevalue">${code}</div>
        <p class="settings-hint">Expires in ~${minutes} minutes. Single use.</p>
      `;
      flash("Sync code ready.");
    } catch (err) {
      console.warn("[settings] showSyncCode failed", err);
      flash("Couldn't create a sync code — check your connection.", "err");
    }
  };

  // We re-render this region whenever the sync state flips. Keeping it
  // self-contained makes the connect/disconnect transitions trivial.
  const renderSync = (): void => {
    syncBody.innerHTML = "";
    if (isSynced()) {
      syncBody.innerHTML = `
        <p class="settings-hint settings-sync-status">Syncing automatically. Use a sync code to bring another device into the same progress.</p>
        <div class="settings-actions">
          <button type="button" class="settings-sync-pair">Create sync code</button>
          <button type="button" class="settings-sync-disconnect">Disconnect</button>
        </div>
        <div class="settings-sync-code" hidden></div>
      `;
      const pairBtn = syncBody.querySelector(
        ".settings-sync-pair",
      ) as HTMLButtonElement;
      const disconnectBtn = syncBody.querySelector(
        ".settings-sync-disconnect",
      ) as HTMLButtonElement;
      const codeBox = syncBody.querySelector(
        ".settings-sync-code",
      ) as HTMLDivElement;

      pairBtn.addEventListener("click", async () => {
        pairBtn.disabled = true;
        await showSyncCode(codeBox);
        pairBtn.disabled = false;
      });

      disconnectBtn.addEventListener("click", () => {
        const ok = window.confirm(
          "Stop syncing this device? Your local progress stays, and the cloud copy stays too — they just won't update each other.",
        );
        if (!ok) return;
        disconnect();
        flash("Disconnected. Local progress is still on this device.");
        renderSync();
        renderLeaderboard();
      });
    } else {
      syncBody.innerHTML = `
        <p class="settings-hint">Sync your progress across devices. Create a code on one device, enter it on the other — after that, every change syncs automatically.</p>
        <div class="settings-actions">
          <button type="button" class="settings-sync-start">Create sync code for new device</button>
          <button type="button" class="settings-sync-enter">Enter sync code from another device</button>
        </div>
        <form class="settings-sync-codeform" hidden>
          <input class="settings-sync-codeinput" type="text" inputmode="numeric" pattern="\\d{6}" maxlength="6" placeholder="000000" autocomplete="off" />
          <button type="submit" class="settings-sync-codesubmit">Sync from code</button>
        </form>
      `;
      const startBtn = syncBody.querySelector(
        ".settings-sync-start",
      ) as HTMLButtonElement;
      const enterBtn = syncBody.querySelector(
        ".settings-sync-enter",
      ) as HTMLButtonElement;
      const form = syncBody.querySelector(
        ".settings-sync-codeform",
      ) as HTMLFormElement;
      const input = syncBody.querySelector(
        ".settings-sync-codeinput",
      ) as HTMLInputElement;

      startBtn.addEventListener("click", async () => {
        startBtn.disabled = true;
        try {
          // Register first (creates the cloud profile + uploads what's
          // on this device), then mint a code so the user can add the
          // other device right away.
          await register();
          renderSync();
          renderLeaderboard();
          const newCodeBox = syncBody.querySelector(
            ".settings-sync-code",
          ) as HTMLDivElement;
          await showSyncCode(newCodeBox);
        } catch (err) {
          console.warn("[settings] register failed", err);
          flash("Couldn't connect to the cloud — check your connection.", "err");
          startBtn.disabled = false;
        }
      });

      enterBtn.addEventListener("click", () => {
        form.hidden = !form.hidden;
        if (!form.hidden) input.focus();
      });

      form.addEventListener("submit", async (e) => {
        e.preventDefault();
        const code = input.value.trim();
        if (!/^\d{6}$/.test(code)) {
          flash("Enter a 6-digit code.", "err");
          return;
        }
        const submit = form.querySelector(
          ".settings-sync-codesubmit",
        ) as HTMLButtonElement;
        submit.disabled = true;
        try {
          await redeemPairCode(code);
          flash("Synced. Progress from the other device is loaded.");
          // Pull may have replaced our local state — refresh inputs.
          state = loadState();
          toleranceInput.value = String(state.settings.fuzzyMatchTolerance);
          toleranceValue.textContent = toleranceInput.value;
          hintsInput.checked = state.settings.showHints;
          renderSync();
          renderLeaderboard();
        } catch (err) {
          console.warn("[settings] redeem failed", err);
          flash("That code didn't work — it may have expired.", "err");
        } finally {
          submit.disabled = false;
        }
      });
    }
  };

  renderSync();

  // ---- leaderboard / username --------------------------------------------

  const flashLb = (msg: string, kind: "ok" | "err" = "ok"): void => {
    lbStatus.textContent = msg;
    lbStatus.dataset.kind = kind;
  };

  const renderLeaderboard = (): void => {
    lbBody.innerHTML = "";
    if (!isSynced()) {
      lbBody.innerHTML = `
        <p class="settings-hint">Connect cloud sync first — your username and arcade scores ride on the same identity.</p>
      `;
      return;
    }
    if (state.username) {
      lbBody.innerHTML = `
        <p class="settings-hint">You're on the global board as <strong>${state.username}</strong>. Your arcade best scores upload automatically.</p>
        <div class="settings-actions">
          <button type="button" class="settings-lb-rename">Change username</button>
        </div>
        <form class="settings-lb-form" hidden>
          <input class="settings-lb-input" type="text" minlength="3" maxlength="20" pattern="[A-Za-z0-9_-]{3,20}" placeholder="new username" autocomplete="off" />
          <button type="submit" class="settings-lb-submit">Claim</button>
        </form>
      `;
    } else {
      lbBody.innerHTML = `
        <p class="settings-hint">Pick a username (3–20 chars, letters/numbers/_/-) to post arcade scores to the global board.</p>
        <form class="settings-lb-form">
          <input class="settings-lb-input" type="text" minlength="3" maxlength="20" pattern="[A-Za-z0-9_-]{3,20}" placeholder="username" autocomplete="off" />
          <button type="submit" class="settings-lb-submit">Claim</button>
        </form>
      `;
    }

    const renameBtn = lbBody.querySelector(
      ".settings-lb-rename",
    ) as HTMLButtonElement | null;
    const form = lbBody.querySelector(".settings-lb-form") as HTMLFormElement;
    const input = lbBody.querySelector(".settings-lb-input") as HTMLInputElement;
    const submit = lbBody.querySelector(".settings-lb-submit") as HTMLButtonElement;

    if (renameBtn) {
      renameBtn.addEventListener("click", () => {
        form.hidden = !form.hidden;
        if (!form.hidden) input.focus();
      });
    }

    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      const username = input.value.trim();
      if (!/^[A-Za-z0-9_-]{3,20}$/.test(username)) {
        flashLb("3–20 chars: letters, numbers, _ or -", "err");
        return;
      }
      submit.disabled = true;
      const result = await claimUsername(username);
      submit.disabled = false;
      if (result.ok) {
        state = { ...state, username: result.username };
        saveState(state);
        flashLb(`Claimed @${result.username}. Your scores will upload from now on.`);
        // Backfill any existing local bests to the board.
        void pushLeaderboardBests(state);
        renderLeaderboard();
      } else if (result.error === "taken") {
        flashLb("That username is taken — try another.", "err");
      } else if (result.error === "invalid_format") {
        flashLb("Invalid format. 3–20 chars: letters, numbers, _ or -", "err");
      } else if (result.error === "reserved") {
        flashLb("That name is reserved.", "err");
      } else if (result.error === "not_synced") {
        flashLb("Connect cloud sync first.", "err");
      } else {
        flashLb("Couldn't reach the server — try again.", "err");
      }
    });
  };

  renderLeaderboard();

  // ---- settings handlers --------------------------------------------------

  const onToleranceChange = (): void => {
    const v = Number.parseInt(toleranceInput.value, 10);
    toleranceValue.textContent = String(v);
    state = { ...state, settings: { ...state.settings, fuzzyMatchTolerance: v } };
    saveState(state);
  };

  const onHintsChange = (): void => {
    state = {
      ...state,
      settings: { ...state.settings, showHints: hintsInput.checked },
    };
    saveState(state);
  };

  const onReset = (): void => {
    const confirmed = window.confirm(
      "Reset all stats, region progress, and settings? This can't be undone.",
    );
    if (!confirmed) return;
    state = resetState();
    toleranceInput.value = String(state.settings.fuzzyMatchTolerance);
    toleranceValue.textContent = toleranceInput.value;
    hintsInput.checked = state.settings.showHints;
    flash("All progress cleared.");
  };

  const onBack = (): void => ctx.nav.goRegionPicker();

  toleranceInput.addEventListener("input", onToleranceChange);
  hintsInput.addEventListener("change", onHintsChange);
  resetBtn.addEventListener("click", onReset);
  back.addEventListener("click", onBack);

  return () => {
    toleranceInput.removeEventListener("input", onToleranceChange);
    hintsInput.removeEventListener("change", onHintsChange);
    resetBtn.removeEventListener("click", onReset);
    back.removeEventListener("click", onBack);
    root.classList.remove("view-settings");
  };
};
